/**
 * PixelSurface — the one place pixels are written.
 *
 * All sprite generation targets this buffer, never a DOM canvas: it runs
 * identically in the browser (boot-time atlas generation) and in Node (the art
 * harness and sheet generator, doc 16 §4) with zero native dependencies, and it
 * is the enforcement point for Law L4 (Snap Law) — every draw coordinate is an
 * integer at internal resolution, or the call throws.
 *
 * Colours are packed 0xRRGGBBAA numbers. Use `rgba()` / `hex()` to build them.
 */

export type Color = number;

/** Blend modes. `set` replaces, `over` is source-over, `add` is additive glow. */
export type Blend = 'set' | 'over' | 'add';

export function rgba(r: number, g: number, b: number, a = 255): Color {
  return (((r & 255) << 24) | ((g & 255) << 16) | ((b & 255) << 8) | (a & 255)) >>> 0;
}

/** `#rgb`, `#rrggbb` or `#rrggbbaa`. */
export function hex(css: string): Color {
  let s = css.trim();
  if (s.startsWith('#')) s = s.slice(1);
  if (s.length === 3) s = s[0]! + s[0]! + s[1]! + s[1]! + s[2]! + s[2]!;
  if (s.length === 6) s += 'ff';
  if (s.length !== 8) throw new Error(`bad colour: ${css}`);
  return parseInt(s, 16) >>> 0;
}

export const R = (c: Color): number => (c >>> 24) & 255;
export const G = (c: Color): number => (c >>> 16) & 255;
export const B = (c: Color): number => (c >>> 8) & 255;
export const A = (c: Color): number => c & 255;

export const TRANSPARENT: Color = 0x00000000;

/** Multiply a colour's RGB by `f` (clamped), keeping alpha. Used for shading ramps. */
export function shade(c: Color, f: number): Color {
  const cl = (v: number): number => (v < 0 ? 0 : v > 255 ? 255 : v | 0);
  return rgba(cl(R(c) * f), cl(G(c) * f), cl(B(c) * f), A(c));
}

/** Linear blend between two colours, `t` in 0..1. */
export function mix(a: Color, b: Color, t: number): Color {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  return rgba(
    R(a) + (R(b) - R(a)) * k,
    G(a) + (G(b) - G(a)) * k,
    B(a) + (B(b) - B(a)) * k,
    A(a) + (A(b) - A(a)) * k,
  );
}

/** Relative luminance 0..1 (used by silhouetteContrast). */
export function luminance(c: Color): number {
  return (0.2126 * R(c) + 0.7152 * G(c) + 0.0722 * B(c)) / 255;
}

function requireInt(name: string, v: number): void {
  if (!Number.isInteger(v)) {
    throw new Error(`L4 Snap Law violation: ${name} = ${v} is not an integer`);
  }
}

export class PixelSurface {
  readonly width: number;
  readonly height: number;
  readonly data: Uint8ClampedArray;

  /** Draw operations issued since the last `resetOps()` — feeds `perfBudget`. */
  ops = 0;

  constructor(width: number, height: number, fill: Color = TRANSPARENT) {
    requireInt('width', width);
    requireInt('height', height);
    if (width <= 0 || height <= 0) throw new Error(`bad surface size ${width}x${height}`);
    this.width = width;
    this.height = height;
    this.data = new Uint8ClampedArray(width * height * 4);
    if (fill !== TRANSPARENT) this.clear(fill);
  }

  resetOps(): this {
    this.ops = 0;
    return this;
  }

  clear(color: Color = TRANSPARENT): this {
    const { data } = this;
    const r = R(color);
    const g = G(color);
    const b = B(color);
    const a = A(color);
    for (let i = 0; i < data.length; i += 4) {
      data[i] = r;
      data[i + 1] = g;
      data[i + 2] = b;
      data[i + 3] = a;
    }
    return this;
  }

  /** Raw read. Returns 0 outside bounds. */
  get(x: number, y: number): Color {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return TRANSPARENT;
    const i = (y * this.width + x) * 4;
    const d = this.data;
    return rgba(d[i]!, d[i + 1]!, d[i + 2]!, d[i + 3]!);
  }

  alphaAt(x: number, y: number): number {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return 0;
    return this.data[(y * this.width + x) * 4 + 3]!;
  }

  /** Single pixel. Coordinates must be integers (L4). */
  px(x: number, y: number, color: Color, blend: Blend = 'over'): this {
    requireInt('x', x);
    requireInt('y', y);
    this.ops++;
    this.write(x, y, color, blend);
    return this;
  }

  private write(x: number, y: number, color: Color, blend: Blend): void {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return;
    const a = A(color);
    if (a === 0 && blend !== 'set') return;
    const i = (y * this.width + x) * 4;
    const d = this.data;
    if (blend === 'set' || (a === 255 && blend === 'over')) {
      d[i] = R(color);
      d[i + 1] = G(color);
      d[i + 2] = B(color);
      d[i + 3] = a;
      return;
    }
    if (blend === 'add') {
      const k = a / 255;
      d[i] = d[i]! + R(color) * k;
      d[i + 1] = d[i + 1]! + G(color) * k;
      d[i + 2] = d[i + 2]! + B(color) * k;
      d[i + 3] = Math.max(d[i + 3]!, a);
      return;
    }
    // source-over
    const sa = a / 255;
    const da = d[i + 3]! / 255;
    const oa = sa + da * (1 - sa);
    if (oa <= 0) {
      d[i] = 0;
      d[i + 1] = 0;
      d[i + 2] = 0;
      d[i + 3] = 0;
      return;
    }
    d[i] = (R(color) * sa + d[i]! * da * (1 - sa)) / oa;
    d[i + 1] = (G(color) * sa + d[i + 1]! * da * (1 - sa)) / oa;
    d[i + 2] = (B(color) * sa + d[i + 2]! * da * (1 - sa)) / oa;
    d[i + 3] = oa * 255;
  }

  /** Horizontal run [x0, x1] inclusive. One draw op. */
  hline(x0: number, x1: number, y: number, color: Color, blend: Blend = 'over'): this {
    requireInt('x0', x0);
    requireInt('x1', x1);
    requireInt('y', y);
    this.ops++;
    if (y < 0 || y >= this.height) return this;
    const a = Math.max(0, Math.min(x0, x1));
    const b = Math.min(this.width - 1, Math.max(x0, x1));
    for (let x = a; x <= b; x++) this.write(x, y, color, blend);
    return this;
  }

  /** Vertical run [y0, y1] inclusive. One draw op. */
  vline(x: number, y0: number, y1: number, color: Color, blend: Blend = 'over'): this {
    requireInt('x', x);
    requireInt('y0', y0);
    requireInt('y1', y1);
    this.ops++;
    if (x < 0 || x >= this.width) return this;
    const a = Math.max(0, Math.min(y0, y1));
    const b = Math.min(this.height - 1, Math.max(y0, y1));
    for (let y = a; y <= b; y++) this.write(x, y, color, blend);
    return this;
  }

  fillRect(x: number, y: number, w: number, h: number, color: Color, blend: Blend = 'over'): this {
    requireInt('x', x);
    requireInt('y', y);
    requireInt('w', w);
    requireInt('h', h);
    this.ops++;
    for (let yy = y; yy < y + h; yy++) {
      if (yy < 0 || yy >= this.height) continue;
      for (let xx = x; xx < x + w; xx++) this.write(xx, yy, color, blend);
    }
    return this;
  }

  strokeRect(x: number, y: number, w: number, h: number, color: Color, blend: Blend = 'over'): this {
    this.hline(x, x + w - 1, y, color, blend);
    this.hline(x, x + w - 1, y + h - 1, color, blend);
    this.vline(x, y + 1, y + h - 2, color, blend);
    this.vline(x + w - 1, y + 1, y + h - 2, color, blend);
    return this;
  }

  /** Copy `src` onto this surface with its top-left at (dx, dy). */
  blit(src: PixelSurface, dx: number, dy: number, blend: Blend = 'over'): this {
    requireInt('dx', dx);
    requireInt('dy', dy);
    this.ops++;
    for (let y = 0; y < src.height; y++) {
      const ty = dy + y;
      if (ty < 0 || ty >= this.height) continue;
      for (let x = 0; x < src.width; x++) {
        const i = (y * src.width + x) * 4;
        const a = src.data[i + 3]!;
        if (a === 0) continue;
        this.write(dx + x, ty, rgba(src.data[i]!, src.data[i + 1]!, src.data[i + 2]!, a), blend);
      }
    }
    return this;
  }

  /** Integer nearest-neighbour upscale. The only scaling this project performs. */
  scaled(factor: number): PixelSurface {
    requireInt('factor', factor);
    if (factor < 1) throw new Error('scale factor must be >= 1');
    const out = new PixelSurface(this.width * factor, this.height * factor);
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        const i = (y * this.width + x) * 4;
        if (this.data[i + 3] === 0) continue;
        const c = rgba(this.data[i]!, this.data[i + 1]!, this.data[i + 2]!, this.data[i + 3]!);
        out.fillRect(x * factor, y * factor, factor, factor, c, 'set');
      }
    }
    return out.resetOps();
  }

  clone(): PixelSurface {
    const out = new PixelSurface(this.width, this.height);
    out.data.set(this.data);
    return out;
  }

  /** 1 where alpha > 0. The input to outline, silhouette and IoU checks. */
  alphaMask(threshold = 0): Uint8Array {
    const m = new Uint8Array(this.width * this.height);
    for (let i = 0; i < m.length; i++) m[i] = this.data[i * 4 + 3]! > threshold ? 1 : 0;
    return m;
  }

  /** Tight bounds of non-transparent pixels, or null if empty. */
  inkBounds(): { x0: number; y0: number; x1: number; y1: number } | null {
    let x0 = this.width;
    let y0 = this.height;
    let x1 = -1;
    let y1 = -1;
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (this.data[(y * this.width + x) * 4 + 3]! === 0) continue;
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
    return x1 < 0 ? null : { x0, y0, x1, y1 };
  }

  /** Every distinct non-transparent colour (feeds `paletteCompliance`). */
  uniqueColors(): Set<Color> {
    const s = new Set<Color>();
    for (let i = 0; i < this.data.length; i += 4) {
      if (this.data[i + 3]! === 0) continue;
      s.add(rgba(this.data[i]!, this.data[i + 1]!, this.data[i + 2]!, this.data[i + 3]!));
    }
    return s;
  }

  /** Browser bridge: an ImageData ready for `createImageBitmap` / texture upload. */
  toImageData(): ImageData {
    return new ImageData(new Uint8ClampedArray(this.data), this.width, this.height);
  }
}
